import React, { ReactNode, useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from "./Tooltip.module.scss";

type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  width?: string;
  height?: string;
  bottom?: string;
  left?: string;
  right?: string;
  tipPos?: boolean;
  hideHover?: boolean;
  className?: string;
};

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  width = "auto",
  height = "auto",
  bottom = "20px",
  left = "0%",
  right = "0%",
  tipPos,
  hideHover,
  className = "",
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateCoords = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  };

  const toggleTooltip = () => {
    if (hideHover) {
      if (!visible) {
        updateCoords();
      }
      setVisible((prev) => !prev);
    }
  };

  const handleMouseEnter = () => {
    if (!hideHover) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      updateCoords();
      setVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!hideHover) {
      timeoutRef.current = setTimeout(() => {
        setVisible(false);
      }, 100);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        hideHover &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [hideHover]);

  // Update coords on scroll to keep tooltip attached (simple version)
  useEffect(() => {
    if (visible) {
      const handleScroll = () => {
        updateCoords();
      };
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [visible]);

  return (
    <>
      <div
        ref={wrapperRef}
        className={`${styles.tooltip_wrapper} ${
          hideHover ? styles.tooltip_with_click : ""
        }`}
        onClick={toggleTooltip}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {visible &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              width: coords.width,
              height: coords.height,
              pointerEvents: "none",
              zIndex: 99999,
            }}
          >
            <div className={`${hideHover ? "tooltip_with_click" : ""}`}>
              <span
                className={`${styles.tooltip} ${className} ${
                  tipPos ? styles.tip_position : ""
                } ${styles.visible}`}
                style={{
                  width,
                  height,
                  bottom,
                  left,
                  right,
                  pointerEvents: "auto",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <span
                  className={`${styles.tooltip_text} ${
                    hideHover ? styles.flex_text : ""
                  }`}
                >
                  {content}
                </span>
              </span>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
